import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";

export const PLANS = [
  {
    id: "equity",
    name: "Equity Analytics",
    monthly: "₹999", monthlyUsd: "~$12",
    quarterly: "₹2,500", quarterlyUsd: "~$30",
    annual: "₹8,000", annualUsd: "~$96",
    features: ["Equity market intelligence", "Quantitative stock screening & analytics", "Market structure analysis", "Market data visualisation"],
  },
  {
    id: "derivatives",
    name: "Equity + Derivatives Analytics",
    featured: true,
    monthly: "₹1,499", monthlyUsd: "~$18",
    quarterly: "₹4,000", quarterlyUsd: "~$48",
    annual: "₹10,000", annualUsd: "~$120",
    features: ["Everything in Equity Analytics", "Index & stock derivatives analytics", "Options chain & open interest analytics", "Volatility analytics"],
  },
  {
    id: "suite",
    name: "Full Intelligence Suite",
    monthly: "₹1,999", monthlyUsd: "~$24",
    quarterly: "₹5,000", quarterlyUsd: "~$60",
    annual: "₹12,000", annualUsd: "~$145",
    features: ["Index / stock / commodity derivatives analytics", "Probability & scenario analysis", "AI-powered news intelligence", "Advanced quantitative research tools"],
  },
];

export default function PricingPlans() {
  return (
    <>
      <div className="grid md:grid-cols-3 gap-5">
        {PLANS.map((p, i) => <PlanCard key={p.id} plan={p} index={i} />)}
      </div>

      <div data-testid="pricing-compliance-note" className="mt-10 rounded-lg border border-white/[0.08] bg-white/[0.015] p-6">
        <div className="label-xs text-ai-accent mb-3">Indicative pricing — not available for purchase</div>
        <p className="text-xs text-zinc-400 leading-relaxed">
          The amounts shown above are indicative planned pricing for future analytics and market intelligence tooling.
          They are displayed for information only. No plan is currently available for purchase, no checkout or payment
          mechanism is offered, and nothing on this page constitutes an offer to sell, or a solicitation to buy, any
          investment, advisory or research service. Product availability and commercial offerings will be announced
          following completion of applicable regulatory and compliance requirements. Pricing is subject to change and to
          applicable taxes.
        </p>
      </div>
    </>
  );
}

function PlanCard({ plan, index }) {
  return (
    <div
      data-testid={`pricing-${plan.id}`}
      className={`relative trace-border rounded-xl p-8 ${plan.featured ? "md:-translate-y-3 ring-1 ring-ai-accent/25" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="overline text-zinc-500">Plan 0{index + 1}</div>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 border border-white/10 rounded-sm px-2 py-1">
          Coming Soon
        </span>
      </div>
      <h3 className="mt-3 font-display font-medium text-2xl tracking-tight">{plan.name}</h3>

      <div className="mt-6 pb-6 border-b border-white/[0.08]">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-4xl tracking-tight text-zinc-300">{plan.monthly}</span>
          <span className="text-sm text-zinc-500">/month</span>
        </div>
        <div className="mt-1 font-mono text-[11px] text-zinc-500 uppercase tracking-[0.14em]">
          {plan.monthlyUsd} · Indicative only
        </div>
      </div>

      <div className="mt-5 space-y-1.5 font-mono text-xs">
        <Row label="QUARTERLY" inr={plan.quarterly} usd={plan.quarterlyUsd} />
        <Row label="ANNUAL" inr={plan.annual} usd={plan.annualUsd} />
      </div>

      <ul className="mt-7 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-ai-accent shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 space-y-3">
        <div className="flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-white/10 bg-white/[0.02] text-sm text-zinc-500">
          <Lock className="w-3.5 h-3.5" /> Currently unavailable
        </div>
        <Link
          to="/early-access"
          data-testid={`pricing-cta-${plan.id}`}
          className={`inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-md font-medium transition-all text-sm ${
            plan.featured ? "brand-gradient text-white hover:brightness-110" : "border border-white/15 text-white hover:bg-white/[0.05]"
          }`}
        >
          Register Your Interest <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Row({ label, inr, usd }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-400">{inr} <span className="text-zinc-600">· {usd}</span></span>
    </div>
  );
}
